import { type ReactNode } from "react";
import { cn } from "../../utils/cn";
import bgLeaf from "../../assets/onboarding/bg-leaf.jpg";
import { FlowingBackdrop } from "./FlowingBackdrop";

/** The photo ground behind both panels: the still image, the color painted
 *  under it while it loads, and an optional gradient laid over it. */
export interface SplitScreenGround {
  image: string;
  color: string;
  /** Where the photo sits when cropped to cover (`background-position`). */
  position?: string;
  /** A CSS gradient painted between the photo and the panels. */
  overlay?: string;
}

/** The Gateway's leaf photograph — the default, shared with onboarding. */
export const LEAF_GROUND: SplitScreenGround = {
  image: bgLeaf,
  color: "#140309",
  position: "left bottom",
};

export interface SplitScreenLayoutProps {
  /**
   * Left panel body over the photo ground — the brand lockup + value-prop on
   * auth screens and the onboarding wizard. Use
   * `text-[color:var(--fg-on-dark)]` / the scoped `.t-*` classes.
   */
  leftPanel: ReactNode;
  /** Right panel body on the light card — the auth form, or the wizard step. */
  children: ReactNode;
  /** Optional top-right slot on the content panel (e.g. a secondary link). */
  topRight?: ReactNode;
  /** Layout classes for the content column, replacing the default
   *  `my-auto max-w-[440px]` (vertically centered auth-card width). Pass e.g.
   *  a wider max-w plus a top margin and `mb-auto` to widen and top-anchor
   *  the column; `w-full` is always applied. */
  contentClassName?: string;
  className?: string;
  /** Photo ground behind the panels; defaults to the Gateway's leaf. */
  ground?: SplitScreenGround;
}

/**
 * The shared onboarding + auth frame, per the Figma guideline (OPSS-22): a
 * near-black ground with the full-bleed leaf photograph, the brand panel on
 * the left, and the content floating on an inset rounded light card —
 * collapsing to a stacked single column below `lg`.
 *
 * The root carries the `ai21-brand` class, which scopes the new AI21 design
 * system (Aeonik type, fuchsia/carmine, no shadows) to this subtree only. See
 * `styles/ai21-brand.css` for the token + boundary rules. This component owns
 * the single page-level `<main>`; consumers render their content into the
 * slots rather than wrapping their own `<main>`.
 */
export function SplitScreenLayout({
  leftPanel,
  children,
  topRight,
  contentClassName,
  className,
  ground = LEAF_GROUND,
}: SplitScreenLayoutProps) {
  // Always light, regardless of the app theme: the photo ground + light card
  // is the onboarding/auth look in both OS modes. The dark-ground remaps are
  // scoped to non-html `.tw-app-dark` carriers (the drawers) so a dark <html>
  // from the dashboard theme can't leak in here.
  return (
    <main
      className={cn(
        // Pinned to the viewport (h-screen): the page never grows; the card
        // scrolls its own overflow instead. Only the left strip + the card
        // margins show the photo ground.
        "ai21-brand tw-app relative flex h-screen w-full flex-col lg:flex-row",
        className,
      )}
      style={{
        background: `${ground.color} url(${ground.image}) ${ground.position ?? "center"} / cover no-repeat`,
        color: "var(--text)",
      }}
    >
      {/* The same photo, animated, painted over the CSS ground above — see
        FlowingBackdrop for what happens when the motion can't run. */}
      <FlowingBackdrop image={ground.image} />
      {ground.overlay && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: ground.overlay }}
        />
      )}

      {/* Left — brand panel over the photo. Takes 32% of the width at lg+;
        hidden below 1024px, where the card takes the full width. Positioned so
        it stacks above the backdrop canvas. */}
      <aside
        className="relative z-10 hidden shrink-0 flex-col justify-between lg:flex lg:w-[32%] lg:px-10 lg:py-11"
        style={{ color: "var(--fg-on-dark)" }}
      >
        {leftPanel}
      </aside>

      {/* Right — the inset light card. The inner div owns the vertical scroll
        (y only) so tall steps scroll while the page and the topRight slot stay
        put. Vertical centering is the content column's auto margins, not
        justify-center — a centered flex child would clip its top once it
        overflows a scroll container. */}
      <div
        className="relative z-10 m-[11px] flex min-h-0 flex-1 flex-col rounded-[12px] lg:ml-0"
        style={{ background: "#fbfbfb" }}
      >
        {topRight && (
          <div className="absolute right-6 top-6 z-10 lg:right-8 lg:top-8">
            {topRight}
          </div>
        )}
        <div className="flex flex-1 flex-col items-center overflow-y-auto px-6 py-12 lg:px-10">
          <div
            className={cn(
              "w-full",
              contentClassName ?? "my-auto max-w-[440px]",
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </main>
  );
}
